import { auth } from '../firebase';

// Store CSRF token in memory (more secure than localStorage)
let currentCSRFToken = null;
let tokenExpiry = null;

// Get CSRF token from server
export const getCSRFToken = async () => {
  try {
    // Check if we have a valid token
    if (currentCSRFToken && tokenExpiry && Date.now() < tokenExpiry) {
      return currentCSRFToken;
    }

    // Get current user's auth token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const authToken = await user.getIdToken();
    
    // Try the authenticated endpoint first
    try {
      const response = await fetch('/api/csrf-token', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        currentCSRFToken = data.csrfToken;
        tokenExpiry = Date.now() + 55 * 60 * 1000; // 55 minutes (less than 1 hour expiry)
        return currentCSRFToken;
      }
    } catch (error) {
    }

    // Fallback to public endpoint
    const response = await fetch('/api/public-csrf-token', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get CSRF token');
    }

    const data = await response.json();
    currentCSRFToken = data.csrfToken;
    tokenExpiry = Date.now() + 55 * 60 * 1000; // 55 minutes
    
    return currentCSRFToken;
    
  } catch (error) {
    console.error('Error getting CSRF token:', error);
    throw error;
  }
};

// Clear CSRF token (call on logout)
export const clearCSRFToken = () => {
  currentCSRFToken = null;
  tokenExpiry = null;
};

// Enhanced fetch wrapper that automatically includes CSRF token for state-changing operations
export const secureApiCall = async (url, options = {}) => {
  const method = options.method || 'GET';
  
  // Check if endpoint is CSRF-exempt
  const csrfExemptEndpoints = [
    '/api/checkUserStatus',
    '/api/validateTutorEmail',
    '/api/getTutorsPublic',
    '/api/getTutorsBySubjects',
    '/api/testTutorEmails',
    '/api/csrf-token',
    '/api/public-csrf-token',
    '/api/oauth',
    '/api/googleAuth',
    '/api/google-signin',
    '/api/completeVerification',
    '/api/completeProfile',
    '/api/deletePendingUser',
    '/api/csp'
  ];
  
  const isExempt = csrfExemptEndpoints.some(endpoint => url.includes(endpoint));
  const needsCSRF = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase()) && !isExempt;
  
  if (needsCSRF && auth.currentUser) {
    try {
      const csrfToken = await getCSRFToken();
      
      options.headers = {
        ...options.headers,
        'X-CSRF-Token': csrfToken
      };
    } catch (error) {
      console.error('Failed to get CSRF token for API call:', error);
      // For critical authentication flows, don't fail silently
      if (url.includes('/api/')) {
      }
    }
  }

  // Add auth token if user is authenticated
  const user = auth.currentUser;
  if (user) {
    try {
      const authToken = await user.getIdToken();
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${authToken}`
      };
    } catch (error) {
    }
  }

  // Default content type
  if (!options.headers?.['Content-Type'] && method !== 'GET') {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json'
    };
  }

  return fetch(url, options);
};

// Utility to check if CSRF token is required for an endpoint
export const requiresCSRF = (url, method = 'GET') => {
  const isStateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase());
  
  // This function is now simplified and only checks the method.
  // The server is the single source of truth for endpoint exemptions.
  return isStateChanging;
};
